import React, { useState, useEffect, useCallback } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { apiGetRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";
import MySuppliersCard from "../../../Components/MySuppliersCard";
import SuppliersHeader from "./components/SuppliersHeader";
import { API_RECORDS_COUNT, SORT_ORDER } from "../../../Constants/config";
import useInfiniteScroll from "../../../hooks/useInfiniteScroll";
import { useDebounce } from "../../../hooks/useDebounce";

function CommunitySuppliers(props) {
  const {
    handleSupplierProfileModal,
    supplierStatusChanged,
    refreshSuppliersData,
    setRefreshSuppliersData,
    supplierStats,
    getSupplierStats,
    categories,
  } = props;

  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const initialTab = Number(searchParams.get("tab")) || 1;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [sortOrder, setSortOrder] = useState(SORT_ORDER.DESC);
  const [suppliers, setSuppliers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [maxPage, setMaxPage] = useState({
    1: 1,
    2: 1,
    3: 1,
  });

  const { debouncedValue: debouncedSearch, clearDebounce } =
    useDebounce(searchValue);

  // generic function for fetching suppliers
  const getSuppliers = async (params = {}) => {
    const {
      activeTab,
      debouncedSearch = "",
      selectedCategories,
      activePage,
      sortOrder,
    } = params;

    try {
      if (activePage === 1) setIsLoading(true);

      const requestParams = {
        type:
          activeTab === 1 ? "active" : activeTab === 2 ? "offers" : "invites",
        pageSize: API_RECORDS_COUNT,
        pageNumber: activePage,
        searchValue: debouncedSearch,
        category_ids: selectedCategories,
        sort_order: sortOrder,
      };

      const response = await apiGetRequest(
        endpoints.getSupplierManagementMembers(requestParams),
      );

      const data = response?.data?.data || [];
      const maxPage = response?.data?.max_page_number ?? 1;

      if (activeTab === 1) {
        if (activePage === 1) {
          setSuppliers(data);
        } else {
          setSuppliers((prevSuppliers) => {
            const allSuppliers = [...prevSuppliers, ...data];
            return allSuppliers;
          });
        }

        setMaxPage((prev) => ({ ...prev, 1: maxPage }));
      } else if (activeTab === 2) {
        if (activePage === 1) {
          setOffers(data);
        } else {
          setOffers((prevOffers) => {
            const allOffers = [...prevOffers, ...data];
            return allOffers;
          });
        }
        setMaxPage((prev) => ({ ...prev, 2: maxPage }));
      } else {
        if (activePage === 1) {
          setInvites(data);
        } else {
          setInvites((prevInvites) => {
            const allInvites = [...prevInvites, ...data];
            return allInvites;
          });
        }
        setMaxPage((prev) => ({ ...prev, 3: maxPage }));
      }
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSuppliersCallback = useCallback(
    async (activePage) => {
      await getSuppliers({
        activeTab,
        debouncedSearch,
        selectedCategories,
        activePage,
        sortOrder,
      });
    },
    [activeTab, debouncedSearch, selectedCategories, sortOrder],
  );
  const { setContainerRef, resetPagination } = useInfiniteScroll({
    fetchMore: fetchSuppliersCallback,
    maxPage: maxPage[activeTab],
    threshold: 200,
    initialPage: 1,
  });

  useEffect(() => {
    resetPagination();
    fetchSuppliersCallback(1);
  }, [fetchSuppliersCallback, resetPagination]);

  useEffect(() => {
    getSupplierStats();
  }, [activeTab, getSupplierStats]);

  useEffect(() => {
    if (refreshSuppliersData) {
      resetPagination();
      fetchSuppliersCallback(1);
      getSupplierStats();
      setRefreshSuppliersData(false);
    }
  }, [
    refreshSuppliersData,
    resetPagination,
    fetchSuppliersCallback,
    setRefreshSuppliersData,
    getSupplierStats,
  ]);

  useEffect(() => {
    document.body.style.overflow = isBelowMd ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isBelowMd]);

  const toggleSortOrder = () => {
    setSortOrder((prev) =>
      prev === SORT_ORDER.DESC ? SORT_ORDER.ASC : SORT_ORDER.DESC,
    );
  };

  const clearFilters = () => {
    setSearchValue("");
    clearDebounce();
    setSelectedCategories([]);
  };

  const hasFilters = selectedCategories.length > 0 || searchValue.trim() !== "";

  return (
    <>
      <SuppliersHeader
        {...{
          activeTab,
          setActiveTab,
          searchValue,
          setSearchValue,
          isLoading,
          categories,
          supplierStats,
          clearFilters,
          hasFilters,
          selectedCategories,
          setSelectedCategories,
        }}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          flexBasis: 1,
          overflow: "hidden",
        }}
      >
        {activeTab === 1 && (
          <MySuppliersCard
            {...{
              data: suppliers,
              tabData: 1,
              activeTab,
              sortOrder,
              toggleSortOrder,
              handleSupplierProfileModal,
              supplierStatusChanged,
              isLoading,
              containerRef: setContainerRef,
            }}
          />
        )}
        {activeTab === 2 && (
          <MySuppliersCard
            {...{
              data: offers,
              tabData: 2,
              activeTab,
              sortOrder,
              toggleSortOrder,
              handleSupplierProfileModal,
              isLoading,
              containerRef: setContainerRef,
            }}
          />
        )}
        {activeTab === 3 && (
          <MySuppliersCard
            {...{
              data: invites,
              tabData: 3,
              activeTab,
              sortOrder,
              toggleSortOrder,
              handleSupplierProfileModal,
              isLoading,
              containerRef: setContainerRef,
            }}
          />
        )}
      </Box>
    </>
  );
}

export default CommunitySuppliers;
